import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AccumulationLimitType,
  ClockStatus,
  GetWasteContainerQuery,
  WasteContainerStatus,
} from "generated-graphql/graphql";
import { ReactNode } from "react";

import { ComplianceFindingBadges } from "../Compliance/ComplianceFindingBadge";
import {
  ACCUMULATION_KINDS,
  useEntityComplianceFindings,
} from "../Compliance/useEntityComplianceFindings";
import { formatContainerDate, formatDaysRemaining, NO_VALUE_PLACEHOLDER } from "./containerDisplay";
import { EmptySectionState, SectionCard } from "./containerSections";
import { WasteContainerClockChip } from "./WasteContainerClockChip";

type WasteContainerDetailData = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type AccumulationLimitRow = WasteContainerDetailData["clock"]["limits"][number];

/**
 * True when the container is actively running a clock (placed, not
 * archived, not shipped) but the facility's generator status couldn't be
 * resolved — the resolver reports this directly via
 * `clock.generatorStatusResolved` rather than being inferred from an empty
 * limits list, since a resolved VSQG facility legitimately produces zero
 * limits too (VSQG central has no federal TIME rule; VSQG satellite is
 * exempt from the SAA volume limit).
 */
function needsGeneratorStatusCallout(container: WasteContainerDetailData): boolean {
  return (
    container.accumulationArea != null &&
    !container.archivedAt &&
    container.status !== WasteContainerStatus.Shipped &&
    !container.clock.generatorStatusResolved
  );
}

function AccumulationLimitRows({ limits }: { limits: AccumulationLimitRow[] }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Basis</TableCell>
          <TableCell>Deadline</TableCell>
          <TableCell>Days remaining</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {limits.map((limit, index) => (
          <TableRow key={`${limit.limitType}-${index}`}>
            <TableCell>{limit.basis}</TableCell>
            <TableCell>{formatContainerDate(limit.deadlineDate)}</TableCell>
            <TableCell>{formatDaysRemaining(limit.daysRemaining)}</TableCell>
            <TableCell>
              <WasteContainerClockChip status={limit.status} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * The governing time limit as a ring: how much of the window is gone, and how
 * much is left in the middle.
 *
 * Only the soonest TIME limit gets the ring — that is the one actually driving
 * the deadline. Any others still list underneath, because a container can sit
 * under a federal and a state clock at once and hiding the slower one would
 * misrepresent what it is bound by.
 */
function AccumulationClockDial({ limit }: { limit: AccumulationLimitRow }) {
  const theme = useTheme();
  const total = limit.totalDays ?? null;
  const remaining = limit.daysRemaining ?? null;
  const used = total !== null && remaining !== null ? Math.min(total, total - remaining) : null;
  // Overdue keeps the ring full rather than wrapping past it, which would read
  // as progress starting over.
  const fraction = total && used !== null ? Math.max(0, Math.min(1, used / total)) : null;
  const overdue = remaining !== null && remaining < 0;
  const ringColor = overdue
    ? theme.palette.error.main
    : limit.status === ClockStatus.Approaching
      ? theme.palette.warning.main
      : theme.palette.success.main;

  return (
    <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap" useFlexGap>
      <Box sx={{ position: "relative", width: 148, height: 148, flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={148}
          thickness={4}
          sx={{ color: "grey.300", position: "absolute", inset: 0 }}
        />
        {fraction !== null && (
          <CircularProgress
            variant="determinate"
            value={fraction * 100}
            size={148}
            thickness={4}
            sx={{ color: ringColor, position: "absolute", inset: 0 }}
          />
        )}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ position: "absolute", inset: 0, textAlign: "center" }}
        >
          <Typography variant="h5" sx={{ lineHeight: 1, fontWeight: 600 }}>
            {remaining === null ? NO_VALUE_PLACEHOLDER : Math.abs(remaining)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {remaining === null ? "" : overdue ? "days overdue" : "days left"}
          </Typography>
        </Stack>
      </Box>
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Deadline
          </Typography>
          <Typography variant="body2">{formatContainerDate(limit.deadlineDate)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            On site
          </Typography>
          <Typography variant="body2">
            {used !== null && total !== null
              ? `${used} of ${total} days`
              : formatDaysRemaining(limit.daysRemaining)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Basis
          </Typography>
          <Typography variant="body2">{limit.basis}</Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

export function AccumulationClockSection({ container }: { container: WasteContainerDetailData }) {
  const { clock } = container;
  // The route is already ContainerTracking-gated, so no extra flag check here.
  const { forContainer } = useEntityComplianceFindings({
    facilityId: container.facilityId,
    kinds: ACCUMULATION_KINDS,
  });

  let body: ReactNode;
  // The soonest dated limit governs; it gets the ring and the rest stay listed.
  const timeLimits = clock.limits
    .filter((limit) => limit.limitType === AccumulationLimitType.Time && limit.deadlineDate)
    .sort((a, b) => (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0));
  const governing = timeLimits[0];
  const others = clock.limits.filter((limit) => limit !== governing);
  if (governing) {
    body = (
      <Stack spacing={3}>
        <AccumulationClockDial limit={governing} />
        {others.length > 0 && <AccumulationLimitRows limits={others} />}
      </Stack>
    );
  } else if (clock.limits.length > 0) {
    body = <AccumulationLimitRows limits={clock.limits} />;
  } else if (needsGeneratorStatusCallout(container)) {
    // Explanatory, with no call to action on purpose: generator status is
    // derived — RCRAInfo's reported status for the facility's EPA id, or the
    // status calculated from its shipment history — so there is no field for
    // the reader to go set. Telling them to set it would send them to a
    // read-only indicator on the facility page.
    body = (
      <Alert severity="info">
        Storage deadlines need this facility&apos;s generator status, which isn&apos;t known yet.
        Generator status comes from the facility&apos;s EPA registration or its shipment history.
      </Alert>
    );
  } else {
    body = <EmptySectionState message="No accumulation clock applies to this container." />;
  }

  return (
    <SectionCard
      title="Accumulation clock"
      adornment={
        // `outlined` to sit beside the outlined clock chips rather than a soft fill.
        <ComplianceFindingBadges findings={forContainer(container.id)} variant="outlined" />
      }
    >
      {body}
    </SectionCard>
  );
}
